import { createContext, useContext, useEffect, useState } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { safeLog } from '@/utils/safeLogger';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  loading: true,
});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    safeLog.init();
    safeLog.dev('🔐 AuthProvider: Setting up auth listener');
    
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (!mounted) return;
        safeLog.info(`🔐 Auth state changed: ${event} User: ${session ? 'authenticated' : 'none'}`);
        safeLog.info(`🔐 Session exists: ${!!session} Token: ${session?.access_token ? 'YES' : 'NO'}`);
        
        setSession(session);
        setUser(session?.user ?? null);
        // Don't set loading=false here - only from getSession (authoritative source)
      }
    );

    // THEN check for existing session - this is the authoritative source
    safeLog.dev('🔐 AuthProvider: Checking for existing session');
    
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!mounted) return;
      safeLog.info(`🔐 Existing session: ${session ? 'found' : 'none'}`);
      
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false); // Only set loading=false here, once
    });

    return () => {
      mounted = false;
      safeLog.dev('🔐 AuthProvider: Cleaning up');
      subscription.unsubscribe();
    };
  }, []);

  return (
    <AuthContext.Provider value={{ user, session, loading }}>
      {children}
    </AuthContext.Provider>
  );
};
